// A date with no time of day: a date of birth, the day a membership starts, a
// block's first and last day. The API keeps only the date part of these, so
// they must travel as the calendar day the person picked ("2014-05-05"), never
// as an instant. TapDatePicker commits local midnight as a UTC ISO string, and
// in British Summer Time that is "2014-05-04T23:00:00.000Z": the API took the
// date part and stored the day before (TEST_T4 H1).
//
// The API sends these back as UTC midnight ("2014-05-05T00:00:00.0000000Z"),
// so reading one with `new Date` and local fields shows the day before
// anywhere west of Greenwich. Every read here takes the date part instead.

const pad = (n) => String(n).padStart(2, '0');

// A bare date, or a date at exactly midnight with or without a zone: the date
// part IS the day (the API's own shape, a seeded "2026-10-18", or a zoneless
// "2026-10-18T00:00:00").
const MIDNIGHT_OR_BARE = /^(\d{4})-(\d{2})-(\d{2})(?:T00:00(?::00(?:\.0+)?)?(?:Z|[+-]00:?00)?)?$/;

/** {year, month (1-12), day} of a date-only value, or null when it isn't one. */
export function dateOnlyParts(value) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'string') {
    const m = MIDNIGHT_OR_BARE.exec(value.trim());
    if (m) return {year: Number(m[1]), month: Number(m[2]), day: Number(m[3])};
  }
  // An instant (the picker's local midnight, a Date): the reader's own calendar day.
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return {year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate()};
}

/** "2014-05-05": what the API is sent for a date-only field. */
export function toDateOnly(value) {
  const p = dateOnlyParts(value);
  return p ? `${p.year}-${pad(p.month)}-${pad(p.day)}` : null;
}

/** Local midnight of the day, as a Date: for showing the day and working out ages. */
export function dateOnlyToLocal(value) {
  const p = dateOnlyParts(value);
  return p ? new Date(p.year, p.month - 1, p.day) : null;
}

/** Local midnight of the day as an ISO string: what TapDatePicker holds for that day. */
export function dateOnlyForPicker(value) {
  const d = dateOnlyToLocal(value);
  return d ? d.toISOString() : null;
}

// How far back a date of birth's calendar goes: a year list of about a century.
export const DATE_OF_BIRTH_YEARS_BACK = 100;

/**
 * TapDatePicker's bounds for a date of birth (R15 N1). The picker's defaults
 * are the scheduling ones (no day before today, Today / Tomorrow / In a week
 * chips, years from now on), which leave every birthday disabled. This window
 * runs from 1 January about a hundred years ago to today, with no chips. Use it
 * with `birth-date date-only`, as AccountSetupFields does with its own window.
 */
export function dateOfBirthPickerBounds(now = new Date()) {
  const today = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  return {min: `${now.getFullYear() - DATE_OF_BIRTH_YEARS_BACK}-01-01`, max: today, quick: []};
}
